/*
  The home statistics band's decisions, as one pure function (spec hero-stats-band.md
  §6.9, §10).

  Everything that could get the band wrong lives here rather than in the component,
  because component tests cannot run in this repo — @testing-library/react 13's `render`
  imports `react-dom/client` and React is 17 (OV§2). `PopulationCard.tsx` (the
  home card that replaced the band, 2026-10-20) is left with "call the function, lay out
  the cells".

  **The `isFinite` guard is the point of this file, not defensive tidiness.** `formatCount`
  renders a missing or non-numeric value as "0" — correct for a per-game count, where zero
  is a true answer, and a lie here: "0 CARDS GRADED" on the public home page is a false
  statement about the business, not a degraded state. `/counts` has no published contract
  (spec §12), so a 200 that drops or renames a field is a real possibility, and
  `PublicCountsDto`'s required `number` fields are a compile-time claim that nothing
  checks at runtime. So every value is re-verified here, and anything that fails hides the
  whole band (H8).

  **Hidden is all-or-nothing, and it is also the loading state.** Two independent queries
  feed this band; either one still resolving, or either one in error, hides all six cells.
  A band that renders a frame and then vanishes is worse than one that was never there —
  PopulationBreakdown settled the same question the same way, and OV§12.23 forbids the
  layout shift.

  The error SHAPE never reaches this function: `isError` is a boolean. Unlike
  parseBreakdown, which has to tell a 404 from a malformed 200, this band has exactly one
  failure behaviour, so there is nothing for a status to disambiguate.
*/

import { isMintGameCode } from "./categories";
import { gradedCountValue } from "./featured";

export interface StatsBandCell {
  key: string;
  label: string;
  value: number;
}

export type StatsBandState =
  | { status: "hidden" }
  | { status: "ready"; cells: StatsBandCell[] };

/* Just enough of GameSummaryDto to count. Keeps this file off the v2 DTO. */
interface CountableGame {
  gameCode: string;
  cardsGraded?: number | null;
}

/*
  Label copy is fixed here (spec §6.7), the way PopulationBreakdown fixes its own under
  D26. Sentence case in the source; the band applies `uppercase` in CSS.

  "Cards graded" carries no qualifier on purpose. It is /counts' global total, which does
  NOT equal the sum of the per-game figures shown further down the same page — so
  "across 63 games" would assert a relationship these numbers do not have (§6.2).
*/
const COUNTS_FIELDS: ReadonlyArray<{ key: string; label: string; field: string }> = [
  { key: "totalGraded", label: "Cards graded", field: "totalGraded" },
  /*
    "Flawless 10" (Robert, 2026-10-20), not "Quad 10": it is the grade the rest of the site
    labels "Flawless 10 (Quad)" (constants/grades.ts), and the public name is Flawless.
    Same field, same count; only the words changed.
  */
  { key: "quad10", label: "Flawless 10", field: "quad10Count" },
  { key: "pristine10", label: "Pristine 10", field: "pristine10Count" },
  { key: "gemMint10", label: "Gem Mint 10", field: "gemMint10Count" },
  { key: "gemMint95", label: "Gem Mint 9.5", field: "gemMint95Count" },
];

const HIDDEN: StatsBandState = { status: "hidden" };

const isRealNumber = (value: unknown): value is number =>
  typeof value === "number" && isFinite(value);

export interface StatsBandInput {
  /* getCounts' data, deliberately unvalidated — the guard above is what validates it. */
  counts: unknown;
  games: CountableGame[] | undefined;
  /* Either query still resolving. */
  isLoading: boolean;
  /* Either query errored, whatever the shape. */
  isError: boolean;
}

export const statsBandState = (input: StatsBandInput): StatsBandState => {
  const { counts, games, isLoading, isError } = input;

  if (isLoading || isError || !games) {
    return HIDDEN;
  }

  if (typeof counts !== "object" || counts === null) {
    return HIDDEN;
  }

  const record = counts as Record<string, unknown>;

  const countsCells: StatsBandCell[] = [];
  for (const { key, label, field } of COUNTS_FIELDS) {
    const value = record[field];
    if (!isRealNumber(value)) {
      return HIDDEN;
    }
    countsCells.push({ key, label, value });
  }

  /*
    Games with at least one card graded, archived ones included. scope=population also
    returns reviewed games with nothing graded yet, and a game with no gradings is not
    one being tracked. The Mint game is barred from every public surface (OVD16, OV§5.2),
    so it is not counted either.

    Catalogue-wide, with no relationship to the A-Z list below it, which shows one
    category section at a time; labelled plainly enough not to imply otherwise (§6.3).
  */
  const gamesValue = games.filter(
    game => !isMintGameCode(game.gameCode) && gradedCountValue(game.cardsGraded) > 0
  ).length;

  return {
    status: "ready",
    cells: [
      { key: "games", label: "Games tracked", value: gamesValue },
      ...countsCells,
    ],
  };
};
